import { useReactiveVar } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useMemo } from 'react'
import { emptyArr } from '../../constants/misc'
import {
  ConjointAttributeValidationError,
  ConjointOptionValidationError,
  EntryType,
  MatrixColumnValidationError,
  MatrixRowValidationError,
  QuestionValidationError,
  ResponseOptionValidationError,
  SliderValidationError,
  ValidationError
} from '../../data/model/questionnaire'
import { getValidationErrors } from '../../modules/Questionnaire/Questionnaire.utils'
import {
  marketReach,
  withMarketAudienceEntries
} from '../../modules/Questionnaire/audienceDisplayLogic'
import { useMarketAudience } from '../../modules/Questionnaire/marketAudienceContext'
import { newEntryId } from '../../utils/questionnaireUtils'
import { responseOptionLkNewlyAdded } from '../useResetNewlyCreatedEntry'
import useGetDraftQuestionnaire from './useGetDraftQuestionnaire'

export interface ValidationResult {
  hasError: boolean
  errorMessage?: string
  errorType?: SliderValidationError
}

interface UseQuestionnaireValidationData {
  hasValidationErrors: () => boolean
  entryHasValidationErrors: (entryId: string) => boolean
  getQuestionValidationErrors: (entryLk: string) => ValidationError | undefined
  getMatrixValidationErrors: (entryLk: string) => ValidationError | undefined
  validateMatrixQuestionTitle: (entryLk: string) => boolean
  validateMatrixRowText: (
    entryLk: string | undefined,
    questionLk: string
  ) => ValidationResult
  validateMatrixColumnText: (
    entryLk: string | undefined,
    responseOptionLk: string
  ) => ValidationResult
  validateBasicResponseOption: (
    entryLk: string | undefined,
    responseOptionLk: string
  ) => ValidationResult
  validateBasicQuestionText: (entryLk: string | undefined) => boolean
  validateSliderNumbers: (entryLk: string | undefined) => ValidationResult
  validateFreeTextResponseRange: (
    entryLk: string | undefined
  ) => ValidationResult
  validateConjointAttributeName: (
    entryId: string,
    attributeId: string
  ) => ValidationResult
  validateConjointOptionValue: (
    entryId: string,
    attributeId: string,
    optionId: string
  ) => ValidationResult
  hasUnclearedValidationErrors: () => {
    invalidAudience: boolean
    invalidQuestionnaire: boolean
  }
  validateQuestionLogic: (entryId: string) => boolean
}

export const validationErrorText = {
  [ResponseOptionValidationError.EmptyResponseOptionValueError]:
    "You can't leave a response option blank",
  [ResponseOptionValidationError.DuplicateResponseOptionValueError]:
    "You can't have a duplicate response option",
  [ResponseOptionValidationError.InvalidRoute]:
    "You can't route a response to a previous question",
  [MatrixColumnValidationError.EmptyMatrixColumnError]:
    "You can't leave a matrix column blank",
  [MatrixColumnValidationError.DuplicateMatrixColumnError]:
    "You can't have a duplicate matrix column",
  [MatrixRowValidationError.EmptyMatrixRowError]:
    "You can't leave a matrix row blank",
  [MatrixRowValidationError.DuplicateMatrixRowError]:
    "You can't have a duplicate matrix row",
  [SliderValidationError.SliderInvalidRangeError]:
    'Highest number should be bigger than lowest number',
  [SliderValidationError.SliderInvalidStepError]:
    'Increment is too big for the range',
  [QuestionValidationError.ResponseRangeMinCharactersExceedError]:
    'Minimum characters cannot be greater than maximum characters',
  [ResponseOptionValidationError.InvalidDisplayLogic]:
    'Please add source for display logic',
  [ConjointAttributeValidationError.EmptyConjointAttributeNameError]:
    "You can't leave an attribute name blank",
  [ConjointAttributeValidationError.DuplicateConjointAttributeNameError]:
    "You can't have duplicate attribute names",
  [ConjointOptionValidationError.EmptyConjointOptionValueError]:
    "You can't leave an option value blank",
  [ConjointOptionValidationError.DuplicateConjointOptionValueError]:
    "You can't have duplicate option values within an attribute"
}

export const isNonEmptyResponseError = (error?: string) => {
  return error !== validationErrorText.EmptyResponseOptionValueError
}

export const isNonEmptyRowError = (error?: string) => {
  return error !== validationErrorText.EmptyMatrixRowError
}

const useQuestionnaireValidation = (): UseQuestionnaireValidationData => {
  const newlyCreatedEntryId = useReactiveVar(newEntryId)
  const newlyAddedResponseOptionLk = useReactiveVar(responseOptionLkNewlyAdded)
  const newlyAddedRowLk = useReactiveVar(responseOptionLkNewlyAdded)

  const {
    flattenedEntries: flattenedQuestionnaireEntries,
    draftQuestionnaire
  } = useGetDraftQuestionnaire()
  const audienceEntries = draftQuestionnaire?.audienceEntries || emptyArr
  const flattenedEntries = useMemo(
    () => flattenedQuestionnaireEntries.concat(audienceEntries),
    [flattenedQuestionnaireEntries, audienceEntries]
  )
  const marketAudience = useMarketAudience()
  const lookupEntries = useMemo(
    () => withMarketAudienceEntries(flattenedEntries, marketAudience),
    [flattenedEntries, marketAudience]
  )
  const reaches = useMemo(
    () => marketReach(marketAudience.index, lookupEntries),
    [marketAudience.index, lookupEntries]
  )

  const audienceValidationErrors = getValidationErrors(
    audienceEntries,
    lookupEntries
  )
  const questionnaireValidationErrors = getValidationErrors(
    flattenedQuestionnaireEntries,
    lookupEntries,
    reaches
  )
  const validationErrors = useMemo(
    () => ({
      ...audienceValidationErrors,
      ...questionnaireValidationErrors
    }),
    [audienceValidationErrors, questionnaireValidationErrors]
  )

  const hasValidationErrors: () => boolean = () => {
    return !isEmpty(validationErrors)
  }

  const getQuestionValidationErrors: (
    entryLk: string
  ) => ValidationError | undefined = (entryLk) => {
    const errors = validationErrors[entryLk]
    return errors
  }

  const getMatrixValidationErrors: (
    entryLk: string
  ) => ValidationError | undefined = useCallback(
    (entryLk) => {
      const errors = validationErrors[entryLk]
      return errors as ValidationError
    },
    [validationErrors]
  )
  const validateBasicQuestionText: (entryLk: string | undefined) => boolean = (
    entryLk
  ) => {
    return (
      (entryLk &&
        newlyCreatedEntryId !== entryLk &&
        getQuestionValidationErrors(entryLk)?.errors?.includes(
          QuestionValidationError.EmptyQuestionTextError
        )) ||
      false
    )
  }

  const validateMatrixQuestionTitle: (entryLk: string) => boolean = (
    entryLk
  ) => {
    // make sure to also check that this is not a newly added item
    return (
      (newlyCreatedEntryId !== entryLk &&
        getMatrixValidationErrors(entryLk)?.errors?.includes(
          QuestionValidationError.EmptyQuestionTextError
        )) ||
      false
    )
  }

  const validateMatrixRowText: (
    entryLk: string | undefined,
    questionLk: string
  ) => ValidationResult = (entryLk, questionLk) => {
    const validationResult: ValidationResult = { hasError: false }

    const rowErrors =
      (entryLk &&
        getMatrixValidationErrors(entryLk)?.rowErrors?.find(
          (error) => error.questionLk === questionLk
        )?.errors) ||
      emptyArr

    const setOfErrors = new Set(rowErrors)

    if (setOfErrors.size > 0) {
      validationResult.hasError = true
    }

    const {
      EmptyMatrixRowError,
      DuplicateMatrixRowError,
      InvalidDisplayLogic
    } = MatrixRowValidationError

    if (setOfErrors.has(EmptyMatrixRowError)) {
      validationResult.errorMessage = validationErrorText[EmptyMatrixRowError]
    } else if (setOfErrors.has(DuplicateMatrixRowError)) {
      validationResult.errorMessage =
        validationErrorText[DuplicateMatrixRowError]
    } else if (setOfErrors.has(InvalidDisplayLogic)) {
      validationResult.errorMessage = validationErrorText[InvalidDisplayLogic]
    }
    return validationResult
  }

  const validateMatrixColumnText: (
    entryLk: string | undefined,
    responseOptionLk: string
  ) => ValidationResult = useCallback(
    (entryLk, responseOptionLk) => {
      const validationResult: ValidationResult = { hasError: false }

      const columnErrors =
        (entryLk &&
          getMatrixValidationErrors(entryLk)?.responseOptionErrors?.find(
            (error) => error.responseOptionLk === responseOptionLk
          )?.errors) ||
        emptyArr

      const setOfErrors = new Set(columnErrors)

      if (setOfErrors.size > 0) {
        validationResult.hasError = true
      }

      const {
        EmptyResponseOptionValueError,
        DuplicateResponseOptionValueError
      } = ResponseOptionValidationError

      if (setOfErrors.has(EmptyResponseOptionValueError)) {
        validationResult.errorMessage =
          validationErrorText[EmptyResponseOptionValueError]
      } else if (setOfErrors.has(DuplicateResponseOptionValueError)) {
        validationResult.errorMessage =
          validationErrorText[DuplicateResponseOptionValueError]
      }

      return validationResult
    },
    [getMatrixValidationErrors]
  )

  const validateBasicResponseOption: (
    entryLk: string | undefined,
    responseOptionLk: string
  ) => ValidationResult = (entryLk, responseOptionLk) => {
    const validationResult: ValidationResult = { hasError: false }

    const responseOptionErrors =
      (entryLk &&
        getQuestionValidationErrors(entryLk)?.responseOptionErrors?.find(
          (error) => error.responseOptionLk === responseOptionLk
        )?.errors) ||
      emptyArr

    const setOfErrors = new Set(responseOptionErrors)

    if (setOfErrors.size > 0) {
      validationResult.hasError = true
    }

    const {
      EmptyResponseOptionValueError,
      DuplicateResponseOptionValueError,
      InvalidRoute,
      InvalidDisplayLogic
    } = ResponseOptionValidationError

    if (setOfErrors.has(EmptyResponseOptionValueError)) {
      validationResult.errorMessage =
        validationErrorText[EmptyResponseOptionValueError]
    } else if (setOfErrors.has(DuplicateResponseOptionValueError)) {
      validationResult.errorMessage =
        validationErrorText[DuplicateResponseOptionValueError]
    } else if (setOfErrors.has(InvalidRoute)) {
      validationResult.errorMessage = validationErrorText[InvalidRoute]
    } else if (setOfErrors.has(InvalidDisplayLogic)) {
      validationResult.errorMessage = validationErrorText[InvalidDisplayLogic]
    }
    return validationResult
  }

  const validateSliderNumbers = (
    entryLk: string | undefined
  ): ValidationResult => {
    const validationResult: ValidationResult = { hasError: false }

    const errors =
      (entryLk && getQuestionValidationErrors(entryLk)?.sliderErrors) ||
      emptyArr

    const setOfErrors = new Set(errors)

    if (setOfErrors.size > 0) {
      validationResult.hasError = true
    }

    const { SliderInvalidRangeError, SliderInvalidStepError } =
      SliderValidationError

    if (setOfErrors.has(SliderInvalidRangeError)) {
      validationResult.errorMessage =
        validationErrorText[SliderInvalidRangeError]
      validationResult.errorType = SliderValidationError.SliderInvalidRangeError
      return validationResult
    }
    if (setOfErrors.has(SliderInvalidStepError)) {
      validationResult.errorMessage =
        validationErrorText[SliderInvalidStepError]
      validationResult.errorType = SliderValidationError.SliderInvalidStepError
    }

    return validationResult
  }

  const validateFreeTextResponseRange = (
    entryLk: string | undefined
  ): ValidationResult => {
    const validationResult: ValidationResult = { hasError: false }

    const { ResponseRangeMinCharactersExceedError } = QuestionValidationError

    const errors =
      (entryLk && getQuestionValidationErrors(entryLk)?.errors) || emptyArr

    const setOfErrors = new Set(errors)

    if (setOfErrors.has(ResponseRangeMinCharactersExceedError)) {
      validationResult.hasError = true
      validationResult.errorMessage =
        validationErrorText[ResponseRangeMinCharactersExceedError]
      return validationResult
    }

    return validationResult
  }

  const hasValidationErrorsInEntry = (
    validationError: ValidationError | undefined
  ): boolean => {
    if (!validationError) {
      return false
    }

    return Boolean(
      validationError.errors?.length ||
        validationError.rowErrors?.length ||
        validationError.responseOptionErrors?.length ||
        validationError.sliderErrors?.length ||
        validationError.forkErrors?.length ||
        validationError.conjointAttributeErrors?.length ||
        validationError.conjointOptionErrors?.length
    )
  }

  const entryHasValidationErrors = (id: string): boolean => {
    const cachedEntry = flattenedEntries.find((entry) => entry.id === id)

    if (!cachedEntry) {
      return false
    }

    switch (cachedEntry.entryType) {
      case EntryType.TextCardEntryType: {
        const isNewlyCreatedEntry = newlyCreatedEntryId === cachedEntry.id
        const entryErrorData = validationErrors[cachedEntry.id]

        return (
          hasValidationErrorsInEntry(entryErrorData) && !isNewlyCreatedEntry
        )
      }

      case EntryType.QuestionEntryType: {
        const cachedEntryItem = cachedEntry.entryItem
        const isNewlyCreatedEntry = newlyCreatedEntryId === cachedEntry.id
        const entryErrorData = validationErrors[cachedEntry.id]

        if (!hasValidationErrorsInEntry(entryErrorData)) {
          return false
        }

        const hasNewlyCreatedResponseOption =
          cachedEntryItem.responseOptions.some(
            (responseOption) =>
              responseOption.responseOptionLk === newlyAddedResponseOptionLk
          )

        return !isNewlyCreatedEntry && !hasNewlyCreatedResponseOption
      }

      case EntryType.MatrixEntryType: {
        const cachedMatrixItem = cachedEntry.entryItem
        const isNewlyCreatedEntry = newlyCreatedEntryId === cachedEntry.id
        const entryErrorData = validationErrors[cachedEntry.id]

        if (!hasValidationErrorsInEntry(entryErrorData)) {
          return false
        }

        const hasNewlyCreatedResponseOption =
          cachedMatrixItem.responseOptions.some(
            (responseOption) =>
              responseOption.responseOptionLk === newlyAddedResponseOptionLk
          )

        const hasNewlyCreatedRow = cachedMatrixItem.matrixRows.some(
          (row) => row.questionLk === newlyAddedRowLk
        )

        return (
          !isNewlyCreatedEntry &&
          !hasNewlyCreatedResponseOption &&
          !hasNewlyCreatedRow
        )
      }

      case EntryType.ForkEntryType: {
        const isNewlyCreatedEntry = newlyCreatedEntryId === cachedEntry.id
        const entryErrorData = validationErrors[cachedEntry.id]

        return (
          hasValidationErrorsInEntry(entryErrorData) && !isNewlyCreatedEntry
        )
      }

      case EntryType.ConjointEntryType: {
        const isNewlyCreatedEntry = newlyCreatedEntryId === cachedEntry.id
        const entryErrorData = validationErrors[cachedEntry.id]

        return (
          hasValidationErrorsInEntry(entryErrorData) && !isNewlyCreatedEntry
        )
      }

      case EntryType.TextHighlighterEntryType: {
        const isNewlyCreatedEntry = newlyCreatedEntryId === cachedEntry.id
        const entryErrorData = validationErrors[cachedEntry.id]

        if (!hasValidationErrorsInEntry(entryErrorData)) {
          return false
        }

        return !isNewlyCreatedEntry
      }

      default:
        return false
    }
  }

  const hasUnclearedValidationErrors = () => {
    return {
      invalidAudience: !isEmpty(audienceValidationErrors),
      invalidQuestionnaire: !isEmpty(questionnaireValidationErrors)
    }
  }

  const validateQuestionLogic = (entryId: string) => {
    return !!getQuestionValidationErrors(entryId)?.errors?.some(
      (error) =>
        error === QuestionValidationError.QuestionLogicInvalidQuestionError
    )
  }

  const validateConjointAttributeName = (
    entryId: string,
    attributeId: string
  ): ValidationResult => {
    const validationResult: ValidationResult = { hasError: false }

    // Skip validation for newly created questions
    if (newlyCreatedEntryId === entryId) {
      return validationResult
    }

    const attributeErrors =
      getQuestionValidationErrors(entryId)?.conjointAttributeErrors?.find(
        (error) => error.attributeId === attributeId
      )?.errors || emptyArr

    const setOfErrors = new Set(attributeErrors)

    if (setOfErrors.size > 0) {
      validationResult.hasError = true
    }

    const {
      EmptyConjointAttributeNameError,
      DuplicateConjointAttributeNameError
    } = ConjointAttributeValidationError

    if (setOfErrors.has(EmptyConjointAttributeNameError)) {
      validationResult.errorMessage =
        validationErrorText[EmptyConjointAttributeNameError]
    } else if (setOfErrors.has(DuplicateConjointAttributeNameError)) {
      validationResult.errorMessage =
        validationErrorText[DuplicateConjointAttributeNameError]
    }

    return validationResult
  }

  const validateConjointOptionValue = (
    entryId: string,
    attributeId: string,
    optionId: string
  ): ValidationResult => {
    const validationResult: ValidationResult = { hasError: false }

    // Skip validation for newly created questions
    if (newlyCreatedEntryId === entryId) {
      return validationResult
    }

    const optionErrors =
      getQuestionValidationErrors(entryId)?.conjointOptionErrors?.find(
        (error) =>
          error.attributeId === attributeId && error.optionId === optionId
      )?.errors || emptyArr

    const setOfErrors = new Set(optionErrors)

    if (setOfErrors.size > 0) {
      validationResult.hasError = true
    }

    const { EmptyConjointOptionValueError, DuplicateConjointOptionValueError } =
      ConjointOptionValidationError

    if (setOfErrors.has(EmptyConjointOptionValueError)) {
      validationResult.errorMessage =
        validationErrorText[EmptyConjointOptionValueError]
    } else if (setOfErrors.has(DuplicateConjointOptionValueError)) {
      validationResult.errorMessage =
        validationErrorText[DuplicateConjointOptionValueError]
    }

    return validationResult
  }

  return {
    hasValidationErrors,
    getQuestionValidationErrors,
    getMatrixValidationErrors,
    entryHasValidationErrors,
    validateMatrixQuestionTitle,
    validateMatrixRowText,
    validateMatrixColumnText,
    validateBasicResponseOption,
    validateBasicQuestionText,
    validateSliderNumbers,
    validateFreeTextResponseRange,
    validateConjointAttributeName,
    validateConjointOptionValue,
    hasUnclearedValidationErrors,
    validateQuestionLogic
  }
}

export default useQuestionnaireValidation
